/**
 * Translations for the commercial workflow (requests, customers, offers,
 * invoices), the dashboard inbox and shared document UI. Deep-merged over
 * the base locale files; German uses Swiss conventions (Offerte, no ß).
 */
export const commercialTranslations = {
  en: {
    nav: { planner: "Planner" },
    common: {
      actions: "Actions",
      amount: "Amount",
      change: "Change",
      clearFilter: "Clear filter",
      country: "Country",
      customer: "Customer",
      description: "Description",
      noMatches: "Nothing matches your search.",
      none: "Nothing here yet.",
      number: "Number",
      pageOf: "Page {{page}} of {{pages}}",
      previous: "Previous",
      priority: "Priority",
      reason: "Reason",
      summary: "Summary",
      type: "Type",
      updated: "Updated",
      viewAll: "View all",
    },
    status: {
      in_review: "In review",
      converted: "Converted",
      cancelled: "Cancelled",
      expired: "Expired",
      superseded: "Superseded",
      archived: "Archived",
      failed: "Failed",
      pending: "Pending",
    },
    search: {
      types: {
        customer: "Customer",
        request: "Request",
        offer: "Offer",
        project: "Project",
        invoice: "Invoice",
        employee: "Employee",
        catalog: "Catalog",
      },
    },
    dashboard: {
      subtitle: "Here is what needs your attention.",
      goodMorning: "Good morning",
      goodDay: "Hello",
      goodEvening: "Good evening",
      searchPlaceholder: "Search customers, requests, offers, invoices…",
      myDay: "My day",
      clockIn: "Not clocked in",
      clockedIn: "Clocked in",
      noAssignments: "No assignments planned for today.",
      outstanding: "Outstanding",
      overdue: "Overdue",
      invoicedThisMonth: "Invoiced this month",
      openOffers: "Open offers",
      cashflow: "Invoiced and paid",
      invoiced: "Invoiced",
      paid: "Paid",
      invoiceCount_one: "{{count}} invoice",
      invoiceCount_other: "{{count}} invoices",
    },
    inbox: {
      title: "To do",
      allDone: "Nothing waiting for you. Well done!",
      items: {
        my_draft_requests_one: "You have {{count}} unfinished request draft",
        my_draft_requests_other: "You have {{count}} unfinished request drafts",
        requests_to_review_one: "{{count}} request waiting for review",
        requests_to_review_other: "{{count}} requests waiting for review",
        offers_to_send_one: "{{count}} offer draft not sent yet",
        offers_to_send_other: "{{count}} offer drafts not sent yet",
        offers_awaiting_response_one:
          "{{count}} offer waiting for the customer",
        offers_awaiting_response_other:
          "{{count}} offers waiting for customers",
        offers_expiring_soon_one: "{{count}} offer expires within 7 days",
        offers_expiring_soon_other: "{{count}} offers expire within 7 days",
        accepted_offers_to_invoice_one:
          "{{count}} accepted offer still to invoice",
        accepted_offers_to_invoice_other:
          "{{count}} accepted offers still to invoice",
        draft_invoices_one: "{{count}} invoice draft not issued",
        draft_invoices_other: "{{count}} invoice drafts not issued",
        overdue_invoices_one: "{{count}} invoice is overdue",
        overdue_invoices_other: "{{count}} invoices are overdue",
        failed_emails_one: "{{count}} email could not be delivered",
        failed_emails_other: "{{count}} emails could not be delivered",
        timesheets_to_approve_one: "{{count}} timesheet to approve",
        timesheets_to_approve_other: "{{count}} timesheets to approve",
        expense_claims_to_pay_one: "{{count}} expense claim to pay",
        expense_claims_to_pay_other: "{{count}} expense claims to pay",
        low_stock_catalog_items_one: "{{count}} catalog item low on stock",
        low_stock_catalog_items_other: "{{count}} catalog items low on stock",
        unscheduled_active_projects_one:
          "{{count}} active project without planned work",
        unscheduled_active_projects_other:
          "{{count}} active projects without planned work",
        scheduled_offboarding_one: "{{count}} employee leaving soon",
        scheduled_offboarding_other: "{{count}} employees leaving soon",
      },
    },
    doc: {
      preview: "Preview",
      pdf: "PDF",
      send: "Send",
      resend: "Resend",
      sendTitle: "Send to customer",
      resendTitle: "Send again",
      sendEmail: "Send email",
      markSent: "Mark as sent without email",
      markSentHint: "For documents handed over in person or sent by post.",
      sendFreezes:
        "The document is frozen when sent. Later changes need a new revision or a credit note.",
      related: "Related",
      noRelated: "No related documents.",
      history: "History",
      introText: "Introduction",
      termsText: "Terms",
      customerNotes: "Note to the customer",
      internalNotes: "Internal notes",
      internalHint: "Only visible to your team.",
      texts: "Texts",
      saved: "All changes saved",
      unsaved: "Unsaved changes",
      createdBy: "Created by {{name}} on {{date}}",
      deliveryPending: "The email is being sent.",
      deliveryFailed: "The email could not be delivered: {{error}}",
    },
    lines: {
      heading: "Positions",
      type: "Type",
      title: "Title",
      description: "Description (optional)",
      textPlaceholder: "Text shown on the document",
      quantity: "Qty",
      unit: "Unit",
      unitPrice: "Unit price ({{currency}})",
      unitPriceShort: "Unit price",
      discount: "Discount",
      discountPercent: "Discount %",
      tax: "VAT",
      lineTotal: "Line total",
      add: "Add position",
      addText: "Add text",
      addFromCatalog: "Add from catalog…",
      fromOffer: "From offer",
      maxQuantity: "At most {{max}} left to invoice",
      types: {
        work: "Work",
        product: "Product",
        material: "Material",
        flat_rate: "Flat rate",
        text: "Text",
        discount: "Discount",
      },
    },
    totals: {
      heading: "Totals",
      subtotal: "Subtotal",
      discount: "Discount",
      net: "Net excl. VAT",
      vatAt: "VAT {{rate}}%",
      rounding: "Rounding",
      total: "Total",
      discountPercent: "Document discount %",
      discountAmount: "Document discount ({{currency}})",
    },
    customerPicker: {
      placeholder: "Search by name, number, phone or address…",
      empty: "No customer found.",
    },
    customer: {
      plural: "Customers",
      new: "New customer",
      edit: "Edit customer",
      listSubtitle: "Everyone you work for, with their sites.",
      searchPlaceholder: "Name, number, phone, address…",
      empty: "No customers yet.",
      active: "Active",
      archived: "Archived",
      archive: "Archive",
      restore: "Restore",
      archivedNotice:
        "This customer is archived and can't be used for new requests or offers.",
      deleteConfirm: "Delete this customer permanently?",
      types: { private: "Private person", company: "Company" },
      companyName: "Company name",
      salutation: "Salutation",
      firstName: "First name",
      lastName: "Last name",
      contact: "Contact",
      contactRequired: "Enter a phone number or an email address.",
      billingAddress: "Billing address",
      streetPlaceholder: "Street and number",
      sites: "Work sites",
      sitesHint:
        "Add further addresses where work takes place (e.g. several properties).",
      noSites: "Work happens at the billing address.",
      addSite: "Add site",
      siteLabel: "Name of the site",
      siteLabelPlaceholder: "e.g. Garden Seestrasse",
      documentLanguage: "Language for documents",
      languageDefault: "Company default",
      duplicatesTitle: "Possible duplicates:",
      match: {
        email: "same email",
        phone: "same phone",
        name_postalCode: "same name",
      },
      createAndUse: "Create and select",
      since: "Customer since {{date}}",
    },
    request: {
      singular: "Request",
      plural: "Requests",
      new: "New request",
      newSubtitle: "Capture the job on site — the office prices it later.",
      listSubtitle: "Jobs captured in the field, from first contact to offer.",
      empty: "No requests yet.",
      scope: { open: "Open", closed: "Closed" },
      onlyMine: "Only mine",
      whatShort: "What",
      when: "When",
      site: "Site",
      priority: "Priority",
      asap: "As soon as possible",
      desiredDate: "Specific date",
      flexible: "flexible",
      stepCustomer: "Customer",
      stepWhere: "Where",
      stepWhat: "What should be done",
      stepWhen: "When and how urgent",
      stepPhotos: "Photos",
      newCustomer: "Create a new customer",
      siteBilling: "Billing address",
      siteOther: "Another address",
      category: "Category",
      subcategory: "Type of work",
      shortDescription: "Short description",
      shortPlaceholder: "e.g. Trim hedge along the street",
      details: "Details",
      detailsPlaceholder: "Size, access, special wishes…",
      timeFrom: "From",
      timeTo: "Until",
      timeFlexible: "Time is flexible",
      priceExpectation: "Price expectation",
      pricing: {
        offer_required: "Offer needed",
        fixed_price: "Fixed price agreed",
        time_and_material: "By effort",
      },
      fixedAmount: "Agreed amount",
      taxIncluded: "Including VAT",
      inclVat: "incl. VAT",
      exclVat: "excl. VAT",
      assignees: "Assigned to",
      unassigned: "Not assigned",
      takePhoto: "Take photo",
      choosePhotos: "Choose files",
      caption: "Caption (optional)",
      photosHint: "Photos help the office price the job correctly.",
      photos: "Photos",
      noPhotos: "No photos yet.",
      uploadPhotos_one: "Upload {{count}} file",
      uploadPhotos_other: "Upload {{count}} files",
      deletePhotoConfirm: "Delete this file?",
      saveDraft: "Save draft",
      submitToOffice: "Submit to office",
      savedDraft: "Draft saved",
      submitted: "Request submitted",
      uploadFailures_one: "{{count}} file could not be uploaded.",
      uploadFailures_other: "{{count}} files could not be uploaded.",
      captureAnother: "Capture another request",
      viewRequest: "View request",
      draftNotice:
        "This request is a draft. Submit it when it's complete so the office sees it.",
      startReview: "Start review",
      createOffer: "Create offer",
      invoiceDirectly: "Invoice directly",
      reopen: "Reopen",
      decline: "Decline",
      declineTitle: "Decline request",
      cancelRequest: "Cancel request",
      cancelTitle: "Cancel request",
      deleteConfirm: "Delete this request and its photos?",
      closedReason: {
        declined: "Declined: {{reason}}",
        cancelled: "Cancelled: {{reason}}",
      },
    },
    offer: {
      singular: "Offer",
      plural: "Offers",
      new: "New offer",
      newSubtitle: "Choose the customer, then add positions.",
      listSubtitle: "Prepare, send and follow up offers.",
      empty: "No offers yet.",
      titlePlaceholder: "e.g. Garden maintenance 2026",
      createDraft: "Create draft",
      fromRequest: "Created from request {{number}}.",
      validUntil: "Valid until",
      accept: "Accept",
      decline: "Decline",
      acceptTitle: "Mark offer as accepted",
      declineTitle: "Mark offer as declined",
      decisionNote: "Note",
      decisionNotePlaceholder: "e.g. Accepted by phone on …",
      revise: "Create revision",
      openRevision: "Open revision",
      openLatest: "Open latest revision",
      previousRevision: "Previous revision",
      nextRevision: "New revision",
      lockedNotice:
        "Sent offers can't be changed. Create a revision to send an updated version.",
      revisionInProgress:
        "A revision of this offer is being prepared. Sending it replaces this one.",
      supersededNotice: "This offer was replaced by a newer revision.",
      acceptedToInvoice: "Accepted — positions are ready to invoice.",
      fullyInvoiced: "Accepted and fully invoiced.",
      createInvoice: "Create invoice",
      deleteDraft: "Delete draft",
      deleteConfirm: "Delete this offer draft?",
    },
    invoice: {
      singular: "Invoice",
      plural: "Invoices",
      new: "New invoice",
      newSubtitle: "Invoice an accepted offer, a request or anything else.",
      listSubtitle: "Issue invoices, track payments and reminders.",
      empty: "No invoices yet.",
      tabs: {
        draft: "Drafts",
        open: "Open",
        overdue: "Overdue",
        paid: "Paid",
        credit_note: "Credit notes",
        cancelled: "Cancelled",
      },
      creditNote: "Credit note",
      draftNumber: "Draft",
      draftCreditNote: "Credit note draft",
      createDraft: "Create draft",
      draftHint:
        "The draft gets its number and date when you issue it. You can edit it until then.",
      fromOffer:
        "All positions of offer {{number}} not yet invoiced are copied.",
      fromRequest: "Invoice for request {{number}}.",
      draftNotice:
        "Draft — edit freely. Issuing assigns the number and date and freezes the invoice.",
      ibanMissing:
        "No bank account is configured. Add the IBAN in Settings → Billing before issuing.",
      issue: "Issue invoice",
      issueCredit: "Issue credit note",
      issueConfirm:
        "Issue this invoice? It gets its final number and can no longer be edited.",
      issueCreditConfirm: "Issue this credit note? It can no longer be edited.",
      dueDate: "Due",
      paid: "Paid",
      credited: "Credited",
      balance: "Open balance",
      creditTotal: "Credit amount",
      billingAddress: "Billing address",
      contactName: "Contact person",
      reference: "Reference",
      paymentTerms: "Payment terms",
      termDays: "Days to pay",
      corrects: "Corrects",
      cancel: "Cancel invoice",
      cancelTitle: "Cancel invoice",
      cancelHint:
        "Cancelling voids the invoice. Use a credit note instead if anything was paid.",
      cancelledNotice: "Cancelled: {{reason}}",
      createCreditNote: "Create credit note",
      creditHint:
        "A draft credit note with all positions is created. Adjust it before issuing.",
      deleteDraft: "Delete draft",
      deleteConfirm: "Delete this draft?",
    },
    payment: {
      plural: "Payments",
      record: "Record payment",
      edit: "Edit payment",
      none: "No payments recorded.",
      amount: "Amount ({{currency}})",
      date: "Payment date",
      method: "Method",
      reference: "Reference",
      deleteConfirm: "Delete this payment?",
      methods: {
        bank_transfer: "Bank transfer",
        cash: "Cash",
        card: "Card",
        twint: "TWINT",
        other: "Other",
      },
    },
    project: { singular: "Project", plural: "Projects" },
    organization: {
      title: "Organization",
      subtitle:
        "Companies, departments and teams. Access rights come from roles.",
      newUnit: "New unit",
      unit: "Unit",
      addChild: "Add sub-unit",
      empty: "No units yet.",
      selectHint: "Select a unit to edit it.",
      kind: "Kind",
      kinds: { company: "Company", department: "Department", team: "Team" },
      parent: "Belongs to",
      noParent: "Top level",
      color: "Colour",
      description: "Description",
      members: "Members",
      noMembers: "No members yet.",
      addMember: "Add member…",
      lead: "Lead",
      leadHint:
        "Leads are shown as contact persons. They get no additional rights.",
    },
    settings: {
      accentColor: "Accent colour",
      accentColorHint: "Used for lines and headings on PDFs.",
      documentTexts: {
        offerIntroText: "Default offer introduction",
        offerTermsText: "Default offer terms",
        invoiceIntroText: "Default invoice introduction",
        invoiceTermsText: "Default payment terms text",
        footerText: "Document footer",
      },
      footerHint: "One line, e.g. company, address and UID.",
      workTime: "Working time",
      workTimeHint:
        "Defaults for new employees' schedules and vacation entitlement.",
      workdayHours: "Hours per workday",
      migration: "Database version",
      emails: "Outgoing emails",
      emailsHint: "Emails are sent in the background and retried on failure.",
      noEmails: "No emails sent yet.",
      retry: "Retry",
    },
    compensation: {
      salaryType: "Salary type",
      monthly: "Monthly salary",
      hourly: "Hourly wage",
      salaryAmount: "Amount",
      salaryBasis: "Amount refers to",
      effectivePensum: "Actual workload",
      fullTime: "Full-time (100%)",
      thirteenth: "13th monthly salary",
    },
    employees: {
      compensation: "Compensation",
      expenses: "Expenses",
      weeklyHours: "Weekly hours",
      workDetails: "Work details",
      workDetailsDescription:
        "Attendance and project allocation for this week.",
    },
    audit: { actor: "Actor", event: "Event" },
    notifications: {
      actor: "Actor",
      actorSearch: "Find actor",
      audit: "Audit log",
      created: "Created",
      entityId: "Entity ID",
      entityType: "Entity type",
      event: "Event",
      metadata: "Metadata",
      noAudit: "No audit events.",
      rows_one: "{{count}} row",
      rows_other: "{{count}} rows",
      summary: "Summary",
    },
    catalog: {
      addComponent: "Add item",
      components: "components",
      defaultLocation: "Default location",
      item: "Item",
      itemsAlwaysTracked: "Items are always tracked in inventory.",
      kind: "Kind",
      noPackageItems: "No items in this package.",
      package: "Package",
      packageItems: "Package items",
      search: "Search catalog",
      stockEnabled: "Track package stock independently",
    },
    planner: {
      projectListHelp:
        "Projects stay in the list so they can be assigned to several people.",
      projects: "Projects",
      team: "Team",
    },
    projects: {
      acceptedOffers: "Accepted offers",
      activity: "Activity",
      actualHours: "Actual hours",
      allCustomers: "All customers",
      allMembers: "All members",
      assignees: "Assigned people",
      assigneesHint:
        "Assign any number of employees. At least one manager is required.",
      budgetVariance: "Budget variance",
      budgetVsActual: "Budget versus actual",
      contingency: "Contingency",
      effectiveHours: "Effective hours",
      excludedCurrencies: "Excluded foreign-currency expenses:",
      expenseBudget: "Expenses",
      expenses: "Expenses",
      financialSummary: "Financial summary",
      hours: "Planned / effective",
      includeChildren: "Include subprojects",
      invoiced: "Invoiced",
      manager: "Manager",
      manualCosts: "Recorded costs",
      margin: "Margin",
      member: "Member",
      missingCostRates:
        "{{labor}} time entries and {{material}} inventory movements have no cost rate.",
      paid: "Paid",
      plannedHours: "Planned hours",
      toggleChildren: "Show or hide subprojects",
      totalCost: "Total cost",
    },
    time: {
      addManualEntry: "Add manual entry",
      clockSessions: "Clock entries",
      compensationBalance: "Compensation balance",
      dayRecorded: "Recorded for selected day",
      editClockEntry: "Edit clock entry",
      manualEntryHint: "Select an entry below to edit its date or times.",
    },
  },

  de: {
    nav: { planner: "Einsatzplanung" },
    common: {
      actions: "Aktionen",
      amount: "Betrag",
      change: "Ändern",
      clearFilter: "Filter entfernen",
      country: "Land",
      customer: "Kunde",
      description: "Beschreibung",
      noMatches: "Keine Treffer für diese Suche.",
      none: "Noch keine Einträge.",
      number: "Nummer",
      pageOf: "Seite {{page}} von {{pages}}",
      previous: "Zurück",
      priority: "Priorität",
      reason: "Grund",
      summary: "Zusammenfassung",
      type: "Typ",
      updated: "Aktualisiert",
      viewAll: "Alle anzeigen",
    },
    status: {
      in_review: "In Prüfung",
      converted: "Übernommen",
      cancelled: "Storniert",
      expired: "Abgelaufen",
      superseded: "Ersetzt",
      archived: "Archiviert",
      failed: "Fehlgeschlagen",
      pending: "Ausstehend",
    },
    search: {
      types: {
        customer: "Kunde",
        request: "Anfrage",
        offer: "Offerte",
        project: "Projekt",
        invoice: "Rechnung",
        employee: "Mitarbeitende",
        catalog: "Katalog",
      },
    },
    dashboard: {
      subtitle: "Das braucht heute deine Aufmerksamkeit.",
      goodMorning: "Guten Morgen",
      goodDay: "Grüezi",
      goodEvening: "Guten Abend",
      searchPlaceholder: "Kunden, Anfragen, Offerten, Rechnungen suchen…",
      myDay: "Mein Tag",
      clockIn: "Nicht eingestempelt",
      clockedIn: "Eingestempelt",
      noAssignments: "Heute sind keine Einsätze geplant.",
      outstanding: "Offen",
      overdue: "Überfällig",
      invoicedThisMonth: "Diesen Monat verrechnet",
      openOffers: "Offene Offerten",
      cashflow: "Verrechnet und bezahlt",
      invoiced: "Verrechnet",
      paid: "Bezahlt",
      invoiceCount_one: "{{count}} Rechnung",
      invoiceCount_other: "{{count}} Rechnungen",
    },
    inbox: {
      title: "Zu erledigen",
      allDone: "Nichts offen. Gut gemacht!",
      items: {
        my_draft_requests_one: "Du hast {{count}} unfertigen Anfrage-Entwurf",
        my_draft_requests_other: "Du hast {{count}} unfertige Anfrage-Entwürfe",
        requests_to_review_one: "{{count}} Anfrage wartet auf Prüfung",
        requests_to_review_other: "{{count}} Anfragen warten auf Prüfung",
        offers_to_send_one: "{{count}} Offerten-Entwurf noch nicht versendet",
        offers_to_send_other:
          "{{count}} Offerten-Entwürfe noch nicht versendet",
        offers_awaiting_response_one: "{{count}} Offerte wartet auf den Kunden",
        offers_awaiting_response_other: "{{count}} Offerten warten auf Kunden",
        offers_expiring_soon_one: "{{count}} Offerte läuft in 7 Tagen ab",
        offers_expiring_soon_other: "{{count}} Offerten laufen in 7 Tagen ab",
        accepted_offers_to_invoice_one:
          "{{count}} angenommene Offerte noch zu verrechnen",
        accepted_offers_to_invoice_other:
          "{{count}} angenommene Offerten noch zu verrechnen",
        draft_invoices_one: "{{count}} Rechnungsentwurf nicht ausgestellt",
        draft_invoices_other: "{{count}} Rechnungsentwürfe nicht ausgestellt",
        overdue_invoices_one: "{{count}} Rechnung ist überfällig",
        overdue_invoices_other: "{{count}} Rechnungen sind überfällig",
        failed_emails_one: "{{count}} E-Mail konnte nicht zugestellt werden",
        failed_emails_other:
          "{{count}} E-Mails konnten nicht zugestellt werden",
        timesheets_to_approve_one: "{{count}} Zeiterfassung zu genehmigen",
        timesheets_to_approve_other: "{{count}} Zeiterfassungen zu genehmigen",
        expense_claims_to_pay_one: "{{count}} Spesenabrechnung auszuzahlen",
        expense_claims_to_pay_other: "{{count}} Spesenabrechnungen auszuzahlen",
        low_stock_catalog_items_one: "{{count}} Artikel mit tiefem Bestand",
        low_stock_catalog_items_other: "{{count}} Artikel mit tiefem Bestand",
        unscheduled_active_projects_one:
          "{{count}} aktives Projekt ohne geplante Einsätze",
        unscheduled_active_projects_other:
          "{{count}} aktive Projekte ohne geplante Einsätze",
        scheduled_offboarding_one: "{{count}} Austritt steht bevor",
        scheduled_offboarding_other: "{{count}} Austritte stehen bevor",
      },
    },
    doc: {
      preview: "Vorschau",
      pdf: "PDF",
      send: "Senden",
      resend: "Erneut senden",
      sendTitle: "An Kunden senden",
      resendTitle: "Erneut senden",
      sendEmail: "E-Mail senden",
      markSent: "Ohne E-Mail als versendet markieren",
      markSentHint:
        "Für persönlich übergebene oder per Post versandte Dokumente.",
      sendFreezes:
        "Beim Versand wird das Dokument eingefroren. Spätere Änderungen erfordern eine Revision oder Gutschrift.",
      related: "Verknüpft",
      noRelated: "Keine verknüpften Dokumente.",
      history: "Verlauf",
      introText: "Einleitung",
      termsText: "Bedingungen",
      customerNotes: "Hinweis an den Kunden",
      internalNotes: "Interne Notizen",
      internalHint: "Nur für dein Team sichtbar.",
      texts: "Texte",
      saved: "Alle Änderungen gespeichert",
      unsaved: "Ungespeicherte Änderungen",
      createdBy: "Erstellt von {{name}} am {{date}}",
      deliveryPending: "Die E-Mail wird versendet.",
      deliveryFailed: "Die E-Mail konnte nicht zugestellt werden: {{error}}",
    },
    lines: {
      heading: "Positionen",
      type: "Art",
      title: "Bezeichnung",
      description: "Beschreibung (optional)",
      textPlaceholder: "Text auf dem Dokument",
      quantity: "Menge",
      unit: "Einheit",
      unitPrice: "Einzelpreis ({{currency}})",
      unitPriceShort: "Einzelpreis",
      discount: "Rabatt",
      discountPercent: "Rabatt %",
      tax: "MWST",
      lineTotal: "Total Position",
      add: "Position hinzufügen",
      addText: "Text hinzufügen",
      addFromCatalog: "Aus Katalog hinzufügen…",
      fromOffer: "Aus Offerte",
      maxQuantity: "Noch höchstens {{max}} zu verrechnen",
      types: {
        work: "Arbeit",
        product: "Produkt",
        material: "Material",
        flat_rate: "Pauschale",
        text: "Text",
        discount: "Rabatt",
      },
    },
    totals: {
      heading: "Total",
      subtotal: "Zwischentotal",
      discount: "Rabatt",
      net: "Netto exkl. MWST",
      vatAt: "MWST {{rate}}%",
      rounding: "Rundung",
      total: "Total",
      discountPercent: "Gesamtrabatt %",
      discountAmount: "Gesamtrabatt ({{currency}})",
    },
    customerPicker: {
      placeholder: "Name, Nummer, Telefon oder Adresse suchen…",
      empty: "Kein Kunde gefunden.",
    },
    customer: {
      plural: "Kunden",
      new: "Neuer Kunde",
      edit: "Kunde bearbeiten",
      listSubtitle: "Alle Kunden mit ihren Einsatzorten.",
      searchPlaceholder: "Name, Nummer, Telefon, Adresse…",
      empty: "Noch keine Kunden.",
      active: "Aktiv",
      archived: "Archiviert",
      archive: "Archivieren",
      restore: "Wiederherstellen",
      archivedNotice:
        "Dieser Kunde ist archiviert und kann nicht für neue Anfragen oder Offerten verwendet werden.",
      deleteConfirm: "Diesen Kunden endgültig löschen?",
      types: { private: "Privatperson", company: "Firma" },
      companyName: "Firmenname",
      salutation: "Anrede",
      firstName: "Vorname",
      lastName: "Nachname",
      contact: "Kontakt",
      contactRequired: "Telefonnummer oder E-Mail-Adresse angeben.",
      billingAddress: "Rechnungsadresse",
      streetPlaceholder: "Strasse und Nummer",
      sites: "Einsatzorte",
      sitesHint:
        "Weitere Adressen, an denen gearbeitet wird (z. B. mehrere Liegenschaften).",
      noSites: "Gearbeitet wird an der Rechnungsadresse.",
      addSite: "Einsatzort hinzufügen",
      siteLabel: "Bezeichnung",
      siteLabelPlaceholder: "z. B. Garten Seestrasse",
      documentLanguage: "Sprache für Dokumente",
      languageDefault: "Firmen-Standard",
      duplicatesTitle: "Mögliche Duplikate:",
      match: {
        email: "gleiche E-Mail",
        phone: "gleiche Telefonnummer",
        name_postalCode: "gleicher Name",
      },
      createAndUse: "Erstellen und auswählen",
      since: "Kunde seit {{date}}",
    },
    request: {
      singular: "Anfrage",
      plural: "Anfragen",
      new: "Neue Anfrage",
      newSubtitle: "Auftrag vor Ort erfassen – das Büro kalkuliert später.",
      listSubtitle:
        "Vor Ort erfasste Aufträge, vom Erstkontakt bis zur Offerte.",
      empty: "Noch keine Anfragen.",
      scope: { open: "Offen", closed: "Abgeschlossen" },
      onlyMine: "Nur meine",
      whatShort: "Was",
      when: "Wann",
      site: "Einsatzort",
      priority: "Priorität",
      asap: "So bald wie möglich",
      desiredDate: "Bestimmtes Datum",
      flexible: "flexibel",
      stepCustomer: "Kunde",
      stepWhere: "Wo",
      stepWhat: "Was ist zu tun",
      stepWhen: "Wann und wie dringend",
      stepPhotos: "Fotos",
      newCustomer: "Neuen Kunden erfassen",
      siteBilling: "Rechnungsadresse",
      siteOther: "Andere Adresse",
      category: "Kategorie",
      subcategory: "Arbeit",
      shortDescription: "Kurzbeschreibung",
      shortPlaceholder: "z. B. Hecke entlang der Strasse schneiden",
      details: "Details",
      detailsPlaceholder: "Grösse, Zugang, besondere Wünsche…",
      timeFrom: "Von",
      timeTo: "Bis",
      timeFlexible: "Zeit ist flexibel",
      priceExpectation: "Preisvorstellung",
      pricing: {
        offer_required: "Offerte nötig",
        fixed_price: "Fixpreis vereinbart",
        time_and_material: "Nach Aufwand",
      },
      fixedAmount: "Vereinbarter Betrag",
      taxIncluded: "Inklusive MWST",
      inclVat: "inkl. MWST",
      exclVat: "exkl. MWST",
      assignees: "Zugewiesen an",
      unassigned: "Niemandem zugewiesen",
      takePhoto: "Foto aufnehmen",
      choosePhotos: "Dateien wählen",
      caption: "Beschriftung (optional)",
      photosHint: "Fotos helfen dem Büro, den Auftrag richtig zu kalkulieren.",
      photos: "Fotos",
      noPhotos: "Noch keine Fotos.",
      uploadPhotos_one: "{{count}} Datei hochladen",
      uploadPhotos_other: "{{count}} Dateien hochladen",
      deletePhotoConfirm: "Diese Datei löschen?",
      saveDraft: "Entwurf speichern",
      submitToOffice: "Ans Büro senden",
      savedDraft: "Entwurf gespeichert",
      submitted: "Anfrage übermittelt",
      uploadFailures_one: "{{count}} Datei konnte nicht hochgeladen werden.",
      uploadFailures_other:
        "{{count}} Dateien konnten nicht hochgeladen werden.",
      captureAnother: "Weitere Anfrage erfassen",
      viewRequest: "Anfrage anzeigen",
      draftNotice:
        "Diese Anfrage ist ein Entwurf. Sende sie ans Büro, sobald sie vollständig ist.",
      startReview: "Prüfung starten",
      createOffer: "Offerte erstellen",
      invoiceDirectly: "Direkt verrechnen",
      reopen: "Wieder öffnen",
      decline: "Ablehnen",
      declineTitle: "Anfrage ablehnen",
      cancelRequest: "Anfrage stornieren",
      cancelTitle: "Anfrage stornieren",
      deleteConfirm: "Diese Anfrage und ihre Fotos löschen?",
      closedReason: {
        declined: "Abgelehnt: {{reason}}",
        cancelled: "Storniert: {{reason}}",
      },
    },
    offer: {
      singular: "Offerte",
      plural: "Offerten",
      new: "Neue Offerte",
      newSubtitle: "Kunde wählen, danach Positionen erfassen.",
      listSubtitle: "Offerten erstellen, versenden und nachfassen.",
      empty: "Noch keine Offerten.",
      titlePlaceholder: "z. B. Gartenunterhalt 2026",
      createDraft: "Entwurf erstellen",
      fromRequest: "Erstellt aus Anfrage {{number}}.",
      validUntil: "Gültig bis",
      accept: "Annehmen",
      decline: "Ablehnen",
      acceptTitle: "Offerte als angenommen markieren",
      declineTitle: "Offerte als abgelehnt markieren",
      decisionNote: "Notiz",
      decisionNotePlaceholder: "z. B. telefonisch angenommen am …",
      revise: "Revision erstellen",
      openRevision: "Revision öffnen",
      openLatest: "Neuste Revision öffnen",
      previousRevision: "Vorherige Revision",
      nextRevision: "Neue Revision",
      lockedNotice:
        "Versendete Offerten sind unveränderlich. Erstelle eine Revision für eine angepasste Version.",
      revisionInProgress:
        "Eine Revision dieser Offerte ist in Arbeit. Beim Versand ersetzt sie diese.",
      supersededNotice:
        "Diese Offerte wurde durch eine neuere Revision ersetzt.",
      acceptedToInvoice:
        "Angenommen – die Positionen können verrechnet werden.",
      fullyInvoiced: "Angenommen und vollständig verrechnet.",
      createInvoice: "Rechnung erstellen",
      deleteDraft: "Entwurf löschen",
      deleteConfirm: "Diesen Offerten-Entwurf löschen?",
    },
    invoice: {
      singular: "Rechnung",
      plural: "Rechnungen",
      new: "Neue Rechnung",
      newSubtitle:
        "Angenommene Offerte, Anfrage oder freie Rechnung verrechnen.",
      listSubtitle: "Rechnungen ausstellen und Zahlungen verfolgen.",
      empty: "Noch keine Rechnungen.",
      tabs: {
        draft: "Entwürfe",
        open: "Offen",
        overdue: "Überfällig",
        paid: "Bezahlt",
        credit_note: "Gutschriften",
        cancelled: "Storniert",
      },
      creditNote: "Gutschrift",
      draftNumber: "Entwurf",
      draftCreditNote: "Gutschrift-Entwurf",
      createDraft: "Entwurf erstellen",
      draftHint:
        "Nummer und Datum erhält der Entwurf beim Ausstellen. Bis dahin kannst du alles ändern.",
      fromOffer:
        "Alle noch nicht verrechneten Positionen der Offerte {{number}} werden übernommen.",
      fromRequest: "Rechnung zur Anfrage {{number}}.",
      draftNotice:
        "Entwurf – frei bearbeitbar. Beim Ausstellen erhält die Rechnung Nummer und Datum und wird eingefroren.",
      ibanMissing:
        "Kein Bankkonto konfiguriert. Erfasse die IBAN unter Einstellungen → Verrechnung.",
      issue: "Rechnung ausstellen",
      issueCredit: "Gutschrift ausstellen",
      issueConfirm:
        "Rechnung ausstellen? Sie erhält ihre definitive Nummer und kann nicht mehr bearbeitet werden.",
      issueCreditConfirm:
        "Gutschrift ausstellen? Sie kann danach nicht mehr bearbeitet werden.",
      dueDate: "Zahlbar bis",
      paid: "Bezahlt",
      credited: "Gutgeschrieben",
      balance: "Offener Betrag",
      creditTotal: "Gutschriftsbetrag",
      billingAddress: "Rechnungsadresse",
      contactName: "Kontaktperson",
      reference: "Referenz",
      paymentTerms: "Zahlungsbedingungen",
      termDays: "Zahlungsfrist (Tage)",
      corrects: "Korrigiert",
      cancel: "Rechnung stornieren",
      cancelTitle: "Rechnung stornieren",
      cancelHint:
        "Eine Stornierung macht die Rechnung ungültig. Wurde bereits etwas bezahlt, erstelle eine Gutschrift.",
      cancelledNotice: "Storniert: {{reason}}",
      createCreditNote: "Gutschrift erstellen",
      creditHint:
        "Es wird ein Gutschrift-Entwurf mit allen Positionen erstellt. Passe ihn vor dem Ausstellen an.",
      deleteDraft: "Entwurf löschen",
      deleteConfirm: "Diesen Entwurf löschen?",
    },
    payment: {
      plural: "Zahlungen",
      record: "Zahlung erfassen",
      edit: "Zahlung bearbeiten",
      none: "Noch keine Zahlungen erfasst.",
      amount: "Betrag ({{currency}})",
      date: "Zahlungsdatum",
      method: "Zahlungsart",
      reference: "Referenz",
      deleteConfirm: "Diese Zahlung löschen?",
      methods: {
        bank_transfer: "Banküberweisung",
        cash: "Bar",
        card: "Karte",
        twint: "TWINT",
        other: "Andere",
      },
    },
    project: { singular: "Projekt", plural: "Projekte" },
    organization: {
      title: "Organisation",
      subtitle:
        "Firmen, Abteilungen und Teams. Zugriffsrechte kommen aus den Rollen.",
      newUnit: "Neue Einheit",
      unit: "Einheit",
      addChild: "Untereinheit hinzufügen",
      empty: "Noch keine Einheiten.",
      selectHint: "Wähle eine Einheit zum Bearbeiten.",
      kind: "Art",
      kinds: { company: "Firma", department: "Abteilung", team: "Team" },
      parent: "Gehört zu",
      noParent: "Oberste Ebene",
      color: "Farbe",
      description: "Beschreibung",
      members: "Mitglieder",
      noMembers: "Noch keine Mitglieder.",
      addMember: "Mitglied hinzufügen…",
      lead: "Leitung",
      leadHint:
        "Leitungen werden als Ansprechpersonen angezeigt und erhalten keine zusätzlichen Rechte.",
    },
    settings: {
      accentColor: "Akzentfarbe",
      accentColorHint: "Für Linien und Titel auf PDFs.",
      documentTexts: {
        offerIntroText: "Standard-Einleitung Offerte",
        offerTermsText: "Standard-Bedingungen Offerte",
        invoiceIntroText: "Standard-Einleitung Rechnung",
        invoiceTermsText: "Standardtext Zahlungsbedingungen",
        footerText: "Fusszeile",
      },
      footerHint: "Eine Zeile, z. B. Firma, Adresse und UID.",
      workTime: "Arbeitszeit",
      workTimeHint:
        "Standardwerte für Arbeitspläne und Ferienanspruch neuer Mitarbeitender.",
      workdayHours: "Stunden pro Arbeitstag",
      migration: "Datenbankversion",
      emails: "Ausgehende E-Mails",
      emailsHint:
        "E-Mails werden im Hintergrund versendet und bei Fehlern erneut versucht.",
      noEmails: "Noch keine E-Mails versendet.",
      retry: "Erneut versuchen",
    },
    compensation: {
      salaryType: "Lohnart",
      monthly: "Monatslohn",
      hourly: "Stundenlohn",
      salaryAmount: "Betrag",
      salaryBasis: "Betrag bezieht sich auf",
      effectivePensum: "Effektives Pensum",
      fullTime: "Vollzeit (100 %)",
      thirteenth: "13. Monatslohn",
    },
    employees: {
      compensation: "Lohn",
      expenses: "Spesen",
      weeklyHours: "Wochenstunden",
      workDetails: "Arbeitsdetails",
      workDetailsDescription: "Anwesenheit und Projektzuteilung dieser Woche.",
    },
    audit: { actor: "Person", event: "Ereignis" },
    notifications: {
      actor: "Person",
      actorSearch: "Person suchen",
      audit: "Protokoll",
      created: "Erstellt",
      entityId: "Objekt-ID",
      entityType: "Objekttyp",
      event: "Ereignis",
      metadata: "Details",
      noAudit: "Keine Protokolleinträge.",
      rows_one: "{{count}} Zeile",
      rows_other: "{{count}} Zeilen",
      summary: "Zusammenfassung",
    },
    catalog: {
      addComponent: "Artikel hinzufügen",
      components: "Bestandteile",
      defaultLocation: "Standard-Lagerort",
      item: "Artikel",
      itemsAlwaysTracked: "Artikel werden immer im Lager geführt.",
      kind: "Art",
      noPackageItems: "Keine Artikel in diesem Paket.",
      package: "Paket",
      packageItems: "Paketinhalt",
      search: "Katalog durchsuchen",
      stockEnabled: "Paketbestand separat führen",
    },
    planner: {
      projectListHelp:
        "Projekte bleiben in der Liste, damit sie mehreren Personen zugewiesen werden können.",
      projects: "Projekte",
      team: "Team",
    },
    projects: {
      acceptedOffers: "Angenommene Offerten",
      activity: "Aktivität",
      actualHours: "Effektive Stunden",
      allCustomers: "Alle Kunden",
      allMembers: "Alle Mitglieder",
      assignees: "Zugewiesene Personen",
      assigneesHint:
        "Beliebig viele Mitarbeitende zuweisen. Mindestens eine Projektleitung ist nötig.",
      budgetVariance: "Budgetabweichung",
      budgetVsActual: "Budget und Ist",
      contingency: "Reserve",
      effectiveHours: "Effektive Stunden",
      excludedCurrencies: "Nicht berücksichtigte Fremdwährungsspesen:",
      expenseBudget: "Spesen",
      expenses: "Spesen",
      financialSummary: "Finanzübersicht",
      hours: "Geplant / effektiv",
      includeChildren: "Unterprojekte einbeziehen",
      invoiced: "Verrechnet",
      manager: "Projektleitung",
      manualCosts: "Erfasste Kosten",
      margin: "Marge",
      member: "Mitglied",
      missingCostRates:
        "{{labor}} Zeiteinträge und {{material}} Lagerbewegungen haben keinen Kostensatz.",
      paid: "Bezahlt",
      plannedHours: "Geplante Stunden",
      toggleChildren: "Unterprojekte ein-/ausblenden",
      totalCost: "Gesamtkosten",
    },
    time: {
      addManualEntry: "Eintrag manuell erfassen",
      clockSessions: "Stempelungen",
      compensationBalance: "Gleitzeitsaldo",
      dayRecorded: "Erfasst am gewählten Tag",
      editClockEntry: "Stempelung bearbeiten",
      manualEntryHint:
        "Wähle unten einen Eintrag, um Datum oder Zeiten zu ändern.",
    },
  },

  fr: {
    nav: { planner: "Planification" },
    common: {
      actions: "Actions",
      amount: "Montant",
      change: "Modifier",
      clearFilter: "Effacer le filtre",
      country: "Pays",
      customer: "Client",
      description: "Description",
      noMatches: "Aucun résultat pour cette recherche.",
      none: "Rien pour l’instant.",
      number: "Numéro",
      pageOf: "Page {{page}} sur {{pages}}",
      previous: "Précédent",
      priority: "Priorité",
      reason: "Motif",
      summary: "Résumé",
      type: "Type",
      updated: "Mis à jour",
      viewAll: "Tout afficher",
    },
    status: {
      in_review: "En examen",
      converted: "Transformée",
      cancelled: "Annulé",
      expired: "Expirée",
      superseded: "Remplacée",
      archived: "Archivé",
      failed: "Échec",
      pending: "En attente",
    },
    search: {
      types: {
        customer: "Client",
        request: "Demande",
        offer: "Offre",
        project: "Projet",
        invoice: "Facture",
        employee: "Collaborateur",
        catalog: "Catalogue",
      },
    },
    dashboard: {
      subtitle: "Voici ce qui demande votre attention.",
      goodMorning: "Bonjour",
      goodDay: "Bonjour",
      goodEvening: "Bonsoir",
      searchPlaceholder: "Rechercher clients, demandes, offres, factures…",
      myDay: "Ma journée",
      clockIn: "Pas pointé",
      clockedIn: "Pointé",
      noAssignments: "Aucune intervention prévue aujourd’hui.",
      outstanding: "Ouvert",
      overdue: "En retard",
      invoicedThisMonth: "Facturé ce mois",
      openOffers: "Offres ouvertes",
      cashflow: "Facturé et encaissé",
      invoiced: "Facturé",
      paid: "Encaissé",
      invoiceCount_one: "{{count}} facture",
      invoiceCount_other: "{{count}} factures",
    },
    inbox: {
      title: "À faire",
      allDone: "Rien en attente. Bravo !",
      items: {
        my_draft_requests_one: "Vous avez {{count}} brouillon de demande",
        my_draft_requests_other: "Vous avez {{count}} brouillons de demande",
        requests_to_review_one: "{{count}} demande à examiner",
        requests_to_review_other: "{{count}} demandes à examiner",
        offers_to_send_one: "{{count}} brouillon d’offre non envoyé",
        offers_to_send_other: "{{count}} brouillons d’offre non envoyés",
        offers_awaiting_response_one: "{{count}} offre en attente de réponse",
        offers_awaiting_response_other:
          "{{count}} offres en attente de réponse",
        offers_expiring_soon_one: "{{count}} offre expire dans 7 jours",
        offers_expiring_soon_other: "{{count}} offres expirent dans 7 jours",
        accepted_offers_to_invoice_one: "{{count}} offre acceptée à facturer",
        accepted_offers_to_invoice_other:
          "{{count}} offres acceptées à facturer",
        draft_invoices_one: "{{count}} brouillon de facture non émis",
        draft_invoices_other: "{{count}} brouillons de facture non émis",
        overdue_invoices_one: "{{count}} facture en retard",
        overdue_invoices_other: "{{count}} factures en retard",
        failed_emails_one: "{{count}} e-mail n’a pas pu être envoyé",
        failed_emails_other: "{{count}} e-mails n’ont pas pu être envoyés",
        timesheets_to_approve_one: "{{count}} feuille de temps à approuver",
        timesheets_to_approve_other: "{{count}} feuilles de temps à approuver",
        expense_claims_to_pay_one: "{{count}} note de frais à rembourser",
        expense_claims_to_pay_other: "{{count}} notes de frais à rembourser",
        low_stock_catalog_items_one: "{{count}} article en stock bas",
        low_stock_catalog_items_other: "{{count}} articles en stock bas",
        unscheduled_active_projects_one:
          "{{count}} projet actif sans intervention planifiée",
        unscheduled_active_projects_other:
          "{{count}} projets actifs sans intervention planifiée",
        scheduled_offboarding_one: "{{count}} départ prochain",
        scheduled_offboarding_other: "{{count}} départs prochains",
      },
    },
    doc: {
      preview: "Aperçu",
      pdf: "PDF",
      send: "Envoyer",
      resend: "Renvoyer",
      sendTitle: "Envoyer au client",
      resendTitle: "Renvoyer",
      sendEmail: "Envoyer l’e-mail",
      markSent: "Marquer comme envoyé sans e-mail",
      markSentHint:
        "Pour les documents remis en main propre ou envoyés par poste.",
      sendFreezes:
        "Le document est figé à l’envoi. Toute modification ultérieure nécessite une révision ou une note de crédit.",
      related: "Documents liés",
      noRelated: "Aucun document lié.",
      history: "Historique",
      introText: "Introduction",
      termsText: "Conditions",
      customerNotes: "Remarque pour le client",
      internalNotes: "Notes internes",
      internalHint: "Visible uniquement par votre équipe.",
      texts: "Textes",
      saved: "Toutes les modifications sont enregistrées",
      unsaved: "Modifications non enregistrées",
      createdBy: "Créé par {{name}} le {{date}}",
      deliveryPending: "L’e-mail est en cours d’envoi.",
      deliveryFailed: "L’e-mail n’a pas pu être remis : {{error}}",
    },
    lines: {
      heading: "Positions",
      type: "Type",
      title: "Désignation",
      description: "Description (facultative)",
      textPlaceholder: "Texte affiché sur le document",
      quantity: "Qté",
      unit: "Unité",
      unitPrice: "Prix unitaire ({{currency}})",
      unitPriceShort: "Prix unitaire",
      discount: "Remise",
      discountPercent: "Remise %",
      tax: "TVA",
      lineTotal: "Total position",
      add: "Ajouter une position",
      addText: "Ajouter un texte",
      addFromCatalog: "Ajouter depuis le catalogue…",
      fromOffer: "De l’offre",
      maxQuantity: "Au plus {{max}} restant à facturer",
      types: {
        work: "Travail",
        product: "Produit",
        material: "Matériel",
        flat_rate: "Forfait",
        text: "Texte",
        discount: "Remise",
      },
    },
    totals: {
      heading: "Totaux",
      subtotal: "Sous-total",
      discount: "Remise",
      net: "Net hors TVA",
      vatAt: "TVA {{rate}} %",
      rounding: "Arrondi",
      total: "Total",
      discountPercent: "Remise globale %",
      discountAmount: "Remise globale ({{currency}})",
    },
    customerPicker: {
      placeholder: "Rechercher par nom, numéro, téléphone ou adresse…",
      empty: "Aucun client trouvé.",
    },
    customer: {
      plural: "Clients",
      new: "Nouveau client",
      edit: "Modifier le client",
      listSubtitle: "Tous vos clients et leurs lieux d’intervention.",
      searchPlaceholder: "Nom, numéro, téléphone, adresse…",
      empty: "Aucun client pour l’instant.",
      active: "Actifs",
      archived: "Archivés",
      archive: "Archiver",
      restore: "Restaurer",
      archivedNotice:
        "Ce client est archivé et ne peut pas être utilisé pour de nouvelles demandes ou offres.",
      deleteConfirm: "Supprimer définitivement ce client ?",
      types: { private: "Particulier", company: "Entreprise" },
      companyName: "Raison sociale",
      salutation: "Civilité",
      firstName: "Prénom",
      lastName: "Nom",
      contact: "Contact",
      contactRequired: "Indiquez un numéro de téléphone ou une adresse e-mail.",
      billingAddress: "Adresse de facturation",
      streetPlaceholder: "Rue et numéro",
      sites: "Lieux d’intervention",
      sitesHint:
        "Ajoutez d’autres adresses où le travail a lieu (p. ex. plusieurs immeubles).",
      noSites: "Le travail a lieu à l’adresse de facturation.",
      addSite: "Ajouter un lieu",
      siteLabel: "Nom du lieu",
      siteLabelPlaceholder: "p. ex. Jardin Seestrasse",
      documentLanguage: "Langue des documents",
      languageDefault: "Par défaut de l’entreprise",
      duplicatesTitle: "Doublons possibles :",
      match: {
        email: "même e-mail",
        phone: "même téléphone",
        name_postalCode: "même nom",
      },
      createAndUse: "Créer et sélectionner",
      since: "Client depuis le {{date}}",
    },
    request: {
      singular: "Demande",
      plural: "Demandes",
      new: "Nouvelle demande",
      newSubtitle:
        "Saisissez le travail sur place – le bureau chiffre ensuite.",
      listSubtitle:
        "Travaux saisis sur le terrain, du premier contact à l’offre.",
      empty: "Aucune demande pour l’instant.",
      scope: { open: "Ouvertes", closed: "Clôturées" },
      onlyMine: "Seulement les miennes",
      whatShort: "Quoi",
      when: "Quand",
      site: "Lieu",
      priority: "Priorité",
      asap: "Dès que possible",
      desiredDate: "Date précise",
      flexible: "flexible",
      stepCustomer: "Client",
      stepWhere: "Où",
      stepWhat: "Que faut-il faire",
      stepWhen: "Quand et avec quelle urgence",
      stepPhotos: "Photos",
      newCustomer: "Créer un nouveau client",
      siteBilling: "Adresse de facturation",
      siteOther: "Autre adresse",
      category: "Catégorie",
      subcategory: "Type de travail",
      shortDescription: "Description courte",
      shortPlaceholder: "p. ex. Tailler la haie le long de la rue",
      details: "Détails",
      detailsPlaceholder: "Surface, accès, souhaits particuliers…",
      timeFrom: "De",
      timeTo: "À",
      timeFlexible: "L’heure est flexible",
      priceExpectation: "Attente de prix",
      pricing: {
        offer_required: "Offre nécessaire",
        fixed_price: "Prix forfaitaire convenu",
        time_and_material: "Selon effort",
      },
      fixedAmount: "Montant convenu",
      taxIncluded: "TVA comprise",
      inclVat: "TVA incl.",
      exclVat: "hors TVA",
      assignees: "Attribuée à",
      unassigned: "Non attribuée",
      takePhoto: "Prendre une photo",
      choosePhotos: "Choisir des fichiers",
      caption: "Légende (facultative)",
      photosHint: "Les photos aident le bureau à chiffrer correctement.",
      photos: "Photos",
      noPhotos: "Aucune photo pour l’instant.",
      uploadPhotos_one: "Téléverser {{count}} fichier",
      uploadPhotos_other: "Téléverser {{count}} fichiers",
      deletePhotoConfirm: "Supprimer ce fichier ?",
      saveDraft: "Enregistrer le brouillon",
      submitToOffice: "Transmettre au bureau",
      savedDraft: "Brouillon enregistré",
      submitted: "Demande transmise",
      uploadFailures_one: "{{count}} fichier n’a pas pu être téléversé.",
      uploadFailures_other: "{{count}} fichiers n’ont pas pu être téléversés.",
      captureAnother: "Saisir une autre demande",
      viewRequest: "Voir la demande",
      draftNotice:
        "Cette demande est un brouillon. Transmettez-la au bureau lorsqu’elle est complète.",
      startReview: "Commencer l’examen",
      createOffer: "Créer une offre",
      invoiceDirectly: "Facturer directement",
      reopen: "Rouvrir",
      decline: "Refuser",
      declineTitle: "Refuser la demande",
      cancelRequest: "Annuler la demande",
      cancelTitle: "Annuler la demande",
      deleteConfirm: "Supprimer cette demande et ses photos ?",
      closedReason: {
        declined: "Refusée : {{reason}}",
        cancelled: "Annulée : {{reason}}",
      },
    },
    offer: {
      singular: "Offre",
      plural: "Offres",
      new: "Nouvelle offre",
      newSubtitle: "Choisissez le client, puis ajoutez les positions.",
      listSubtitle: "Préparer, envoyer et suivre les offres.",
      empty: "Aucune offre pour l’instant.",
      titlePlaceholder: "p. ex. Entretien du jardin 2026",
      createDraft: "Créer le brouillon",
      fromRequest: "Créée à partir de la demande {{number}}.",
      validUntil: "Valable jusqu’au",
      accept: "Accepter",
      decline: "Refuser",
      acceptTitle: "Marquer l’offre comme acceptée",
      declineTitle: "Marquer l’offre comme refusée",
      decisionNote: "Remarque",
      decisionNotePlaceholder: "p. ex. acceptée par téléphone le …",
      revise: "Créer une révision",
      openRevision: "Ouvrir la révision",
      openLatest: "Ouvrir la dernière révision",
      previousRevision: "Révision précédente",
      nextRevision: "Nouvelle révision",
      lockedNotice:
        "Une offre envoyée ne peut plus être modifiée. Créez une révision pour envoyer une version adaptée.",
      revisionInProgress:
        "Une révision de cette offre est en préparation. Son envoi remplacera celle-ci.",
      supersededNotice:
        "Cette offre a été remplacée par une révision plus récente.",
      acceptedToInvoice: "Acceptée – les positions peuvent être facturées.",
      fullyInvoiced: "Acceptée et entièrement facturée.",
      createInvoice: "Créer une facture",
      deleteDraft: "Supprimer le brouillon",
      deleteConfirm: "Supprimer ce brouillon d’offre ?",
    },
    invoice: {
      singular: "Facture",
      plural: "Factures",
      new: "Nouvelle facture",
      newSubtitle: "Facturer une offre acceptée, une demande ou autre chose.",
      listSubtitle: "Émettre des factures et suivre les paiements.",
      empty: "Aucune facture pour l’instant.",
      tabs: {
        draft: "Brouillons",
        open: "Ouvertes",
        overdue: "En retard",
        paid: "Payées",
        credit_note: "Notes de crédit",
        cancelled: "Annulées",
      },
      creditNote: "Note de crédit",
      draftNumber: "Brouillon",
      draftCreditNote: "Brouillon de note de crédit",
      createDraft: "Créer le brouillon",
      draftHint:
        "Le numéro et la date sont attribués à l’émission. Jusque-là, tout reste modifiable.",
      fromOffer:
        "Toutes les positions non encore facturées de l’offre {{number}} sont reprises.",
      fromRequest: "Facture pour la demande {{number}}.",
      draftNotice:
        "Brouillon – librement modifiable. L’émission attribue numéro et date et fige la facture.",
      ibanMissing:
        "Aucun compte bancaire configuré. Saisissez l’IBAN dans Paramètres → Facturation.",
      issue: "Émettre la facture",
      issueCredit: "Émettre la note de crédit",
      issueConfirm:
        "Émettre cette facture ? Elle reçoit son numéro définitif et ne peut plus être modifiée.",
      issueCreditConfirm:
        "Émettre cette note de crédit ? Elle ne pourra plus être modifiée.",
      dueDate: "Échéance",
      paid: "Payé",
      credited: "Crédité",
      balance: "Solde ouvert",
      creditTotal: "Montant crédité",
      billingAddress: "Adresse de facturation",
      contactName: "Personne de contact",
      reference: "Référence",
      paymentTerms: "Conditions de paiement",
      termDays: "Délai de paiement (jours)",
      corrects: "Corrige",
      cancel: "Annuler la facture",
      cancelTitle: "Annuler la facture",
      cancelHint:
        "L’annulation rend la facture caduque. Si un paiement a déjà été reçu, créez plutôt une note de crédit.",
      cancelledNotice: "Annulée : {{reason}}",
      createCreditNote: "Créer une note de crédit",
      creditHint:
        "Un brouillon de note de crédit avec toutes les positions est créé. Adaptez-le avant de l’émettre.",
      deleteDraft: "Supprimer le brouillon",
      deleteConfirm: "Supprimer ce brouillon ?",
    },
    payment: {
      plural: "Paiements",
      record: "Saisir un paiement",
      edit: "Modifier le paiement",
      none: "Aucun paiement saisi.",
      amount: "Montant ({{currency}})",
      date: "Date de paiement",
      method: "Mode de paiement",
      reference: "Référence",
      deleteConfirm: "Supprimer ce paiement ?",
      methods: {
        bank_transfer: "Virement bancaire",
        cash: "Espèces",
        card: "Carte",
        twint: "TWINT",
        other: "Autre",
      },
    },
    project: { singular: "Projet", plural: "Projets" },
    organization: {
      title: "Organisation",
      subtitle:
        "Entreprises, départements et équipes. Les droits d’accès proviennent des rôles.",
      newUnit: "Nouvelle unité",
      unit: "Unité",
      addChild: "Ajouter une sous-unité",
      empty: "Aucune unité pour l’instant.",
      selectHint: "Sélectionnez une unité pour la modifier.",
      kind: "Type",
      kinds: {
        company: "Entreprise",
        department: "Département",
        team: "Équipe",
      },
      parent: "Rattachée à",
      noParent: "Niveau supérieur",
      color: "Couleur",
      description: "Description",
      members: "Membres",
      noMembers: "Aucun membre pour l’instant.",
      addMember: "Ajouter un membre…",
      lead: "Responsable",
      leadHint:
        "Les responsables sont affichés comme interlocuteurs et n’obtiennent aucun droit supplémentaire.",
    },
    settings: {
      accentColor: "Couleur d’accent",
      accentColorHint: "Utilisée pour les lignes et titres des PDF.",
      documentTexts: {
        offerIntroText: "Introduction par défaut des offres",
        offerTermsText: "Conditions par défaut des offres",
        invoiceIntroText: "Introduction par défaut des factures",
        invoiceTermsText: "Texte par défaut des conditions de paiement",
        footerText: "Pied de page",
      },
      footerHint: "Une ligne, p. ex. entreprise, adresse et IDE.",
      workTime: "Temps de travail",
      workTimeHint:
        "Valeurs par défaut pour les horaires et vacances des nouveaux collaborateurs.",
      workdayHours: "Heures par jour de travail",
      migration: "Version de la base de données",
      emails: "E-mails sortants",
      emailsHint:
        "Les e-mails sont envoyés en arrière-plan et renvoyés en cas d’échec.",
      noEmails: "Aucun e-mail envoyé pour l’instant.",
      retry: "Réessayer",
    },
    compensation: {
      salaryType: "Type de salaire",
      monthly: "Salaire mensuel",
      hourly: "Salaire horaire",
      salaryAmount: "Montant",
      salaryBasis: "Le montant se réfère à",
      effectivePensum: "Taux d’occupation effectif",
      fullTime: "Plein temps (100 %)",
      thirteenth: "13e salaire",
    },
    employees: {
      compensation: "Salaire",
      expenses: "Frais",
      weeklyHours: "Heures hebdomadaires",
      workDetails: "Détails du travail",
      workDetailsDescription:
        "Présence et répartition par projet de cette semaine.",
    },
    audit: { actor: "Auteur", event: "Événement" },
    notifications: {
      actor: "Auteur",
      actorSearch: "Rechercher un auteur",
      audit: "Journal",
      created: "Créé",
      entityId: "ID de l’objet",
      entityType: "Type d’objet",
      event: "Événement",
      metadata: "Détails",
      noAudit: "Aucun événement.",
      rows_one: "{{count}} ligne",
      rows_other: "{{count}} lignes",
      summary: "Résumé",
    },
    catalog: {
      addComponent: "Ajouter un article",
      components: "composants",
      defaultLocation: "Emplacement par défaut",
      item: "Article",
      itemsAlwaysTracked: "Les articles sont toujours gérés en stock.",
      kind: "Type",
      noPackageItems: "Aucun article dans ce lot.",
      package: "Lot",
      packageItems: "Contenu du lot",
      search: "Rechercher dans le catalogue",
      stockEnabled: "Gérer le stock du lot séparément",
    },
    planner: {
      projectListHelp:
        "Les projets restent dans la liste pour pouvoir être attribués à plusieurs personnes.",
      projects: "Projets",
      team: "Équipe",
    },
    projects: {
      acceptedOffers: "Offres acceptées",
      activity: "Activité",
      actualHours: "Heures effectives",
      allCustomers: "Tous les clients",
      allMembers: "Tous les membres",
      assignees: "Personnes attribuées",
      assigneesHint:
        "Attribuez autant de collaborateurs que nécessaire. Au moins un responsable est requis.",
      budgetVariance: "Écart budgétaire",
      budgetVsActual: "Budget et réel",
      contingency: "Réserve",
      effectiveHours: "Heures effectives",
      excludedCurrencies: "Frais en devises étrangères non pris en compte :",
      expenseBudget: "Frais",
      expenses: "Frais",
      financialSummary: "Résumé financier",
      hours: "Prévu / effectif",
      includeChildren: "Inclure les sous-projets",
      invoiced: "Facturé",
      manager: "Responsable",
      manualCosts: "Coûts saisis",
      margin: "Marge",
      member: "Membre",
      missingCostRates:
        "{{labor}} saisies de temps et {{material}} mouvements de stock n’ont pas de taux de coût.",
      paid: "Payé",
      plannedHours: "Heures prévues",
      toggleChildren: "Afficher ou masquer les sous-projets",
      totalCost: "Coût total",
    },
    time: {
      addManualEntry: "Ajouter une saisie manuelle",
      clockSessions: "Pointages",
      compensationBalance: "Solde d’heures",
      dayRecorded: "Saisi pour le jour choisi",
      editClockEntry: "Modifier le pointage",
      manualEntryHint:
        "Sélectionnez une saisie ci-dessous pour modifier sa date ou ses heures.",
    },
  },

  es: {
    nav: { planner: "Planificación" },
    common: {
      actions: "Acciones",
      amount: "Importe",
      change: "Cambiar",
      clearFilter: "Quitar filtro",
      country: "País",
      customer: "Cliente",
      description: "Descripción",
      noMatches: "No hay resultados para esta búsqueda.",
      none: "Nada por aquí todavía.",
      number: "Número",
      pageOf: "Página {{page}} de {{pages}}",
      previous: "Anterior",
      priority: "Prioridad",
      reason: "Motivo",
      summary: "Resumen",
      type: "Tipo",
      updated: "Actualizado",
      viewAll: "Ver todo",
    },
    status: {
      in_review: "En revisión",
      converted: "Convertida",
      cancelled: "Cancelado",
      expired: "Caducada",
      superseded: "Sustituida",
      archived: "Archivado",
      failed: "Fallido",
      pending: "Pendiente",
    },
    search: {
      types: {
        customer: "Cliente",
        request: "Solicitud",
        offer: "Oferta",
        project: "Proyecto",
        invoice: "Factura",
        employee: "Empleado",
        catalog: "Catálogo",
      },
    },
    dashboard: {
      subtitle: "Esto es lo que requiere tu atención.",
      goodMorning: "Buenos días",
      goodDay: "Hola",
      goodEvening: "Buenas tardes",
      searchPlaceholder: "Buscar clientes, solicitudes, ofertas, facturas…",
      myDay: "Mi día",
      clockIn: "Sin fichar",
      clockedIn: "Fichado",
      noAssignments: "No hay trabajos planificados para hoy.",
      outstanding: "Pendiente de cobro",
      overdue: "Vencido",
      invoicedThisMonth: "Facturado este mes",
      openOffers: "Ofertas abiertas",
      cashflow: "Facturado y cobrado",
      invoiced: "Facturado",
      paid: "Cobrado",
      invoiceCount_one: "{{count}} factura",
      invoiceCount_other: "{{count}} facturas",
    },
    inbox: {
      title: "Pendiente",
      allDone: "No hay nada pendiente. ¡Bien hecho!",
      items: {
        my_draft_requests_one: "Tienes {{count}} borrador de solicitud",
        my_draft_requests_other: "Tienes {{count}} borradores de solicitud",
        requests_to_review_one: "{{count}} solicitud por revisar",
        requests_to_review_other: "{{count}} solicitudes por revisar",
        offers_to_send_one: "{{count}} borrador de oferta sin enviar",
        offers_to_send_other: "{{count}} borradores de oferta sin enviar",
        offers_awaiting_response_one: "{{count}} oferta esperando respuesta",
        offers_awaiting_response_other: "{{count}} ofertas esperando respuesta",
        offers_expiring_soon_one: "{{count}} oferta caduca en 7 días",
        offers_expiring_soon_other: "{{count}} ofertas caducan en 7 días",
        accepted_offers_to_invoice_one:
          "{{count}} oferta aceptada por facturar",
        accepted_offers_to_invoice_other:
          "{{count}} ofertas aceptadas por facturar",
        draft_invoices_one: "{{count}} borrador de factura sin emitir",
        draft_invoices_other: "{{count}} borradores de factura sin emitir",
        overdue_invoices_one: "{{count}} factura vencida",
        overdue_invoices_other: "{{count}} facturas vencidas",
        failed_emails_one: "{{count}} correo no se pudo entregar",
        failed_emails_other: "{{count}} correos no se pudieron entregar",
        timesheets_to_approve_one: "{{count}} hoja de horas por aprobar",
        timesheets_to_approve_other: "{{count}} hojas de horas por aprobar",
        expense_claims_to_pay_one: "{{count}} nota de gastos por pagar",
        expense_claims_to_pay_other: "{{count}} notas de gastos por pagar",
        low_stock_catalog_items_one: "{{count}} artículo con poco stock",
        low_stock_catalog_items_other: "{{count}} artículos con poco stock",
        unscheduled_active_projects_one:
          "{{count}} proyecto activo sin trabajos planificados",
        unscheduled_active_projects_other:
          "{{count}} proyectos activos sin trabajos planificados",
        scheduled_offboarding_one: "{{count}} baja próxima",
        scheduled_offboarding_other: "{{count}} bajas próximas",
      },
    },
    doc: {
      preview: "Vista previa",
      pdf: "PDF",
      send: "Enviar",
      resend: "Reenviar",
      sendTitle: "Enviar al cliente",
      resendTitle: "Reenviar",
      sendEmail: "Enviar correo",
      markSent: "Marcar como enviado sin correo",
      markSentHint:
        "Para documentos entregados en persona o por correo postal.",
      sendFreezes:
        "El documento se congela al enviarlo. Los cambios posteriores requieren una revisión o una nota de crédito.",
      related: "Relacionado",
      noRelated: "No hay documentos relacionados.",
      history: "Historial",
      introText: "Introducción",
      termsText: "Condiciones",
      customerNotes: "Nota para el cliente",
      internalNotes: "Notas internas",
      internalHint: "Solo visible para tu equipo.",
      texts: "Textos",
      saved: "Todos los cambios guardados",
      unsaved: "Cambios sin guardar",
      createdBy: "Creado por {{name}} el {{date}}",
      deliveryPending: "El correo se está enviando.",
      deliveryFailed: "No se pudo entregar el correo: {{error}}",
    },
    lines: {
      heading: "Posiciones",
      type: "Tipo",
      title: "Concepto",
      description: "Descripción (opcional)",
      textPlaceholder: "Texto que aparece en el documento",
      quantity: "Cant.",
      unit: "Unidad",
      unitPrice: "Precio unitario ({{currency}})",
      unitPriceShort: "Precio unitario",
      discount: "Descuento",
      discountPercent: "Descuento %",
      tax: "IVA",
      lineTotal: "Total posición",
      add: "Añadir posición",
      addText: "Añadir texto",
      addFromCatalog: "Añadir del catálogo…",
      fromOffer: "De la oferta",
      maxQuantity: "Quedan como máximo {{max}} por facturar",
      types: {
        work: "Trabajo",
        product: "Producto",
        material: "Material",
        flat_rate: "Tarifa plana",
        text: "Texto",
        discount: "Descuento",
      },
    },
    totals: {
      heading: "Totales",
      subtotal: "Subtotal",
      discount: "Descuento",
      net: "Neto sin IVA",
      vatAt: "IVA {{rate}} %",
      rounding: "Redondeo",
      total: "Total",
      discountPercent: "Descuento global %",
      discountAmount: "Descuento global ({{currency}})",
    },
    customerPicker: {
      placeholder: "Buscar por nombre, número, teléfono o dirección…",
      empty: "No se encontró ningún cliente.",
    },
    customer: {
      plural: "Clientes",
      new: "Nuevo cliente",
      edit: "Editar cliente",
      listSubtitle: "Todos tus clientes con sus lugares de trabajo.",
      searchPlaceholder: "Nombre, número, teléfono, dirección…",
      empty: "Todavía no hay clientes.",
      active: "Activos",
      archived: "Archivados",
      archive: "Archivar",
      restore: "Restaurar",
      archivedNotice:
        "Este cliente está archivado y no se puede usar para nuevas solicitudes u ofertas.",
      deleteConfirm: "¿Eliminar este cliente definitivamente?",
      types: { private: "Particular", company: "Empresa" },
      companyName: "Nombre de la empresa",
      salutation: "Tratamiento",
      firstName: "Nombre",
      lastName: "Apellido",
      contact: "Contacto",
      contactRequired: "Introduce un teléfono o un correo electrónico.",
      billingAddress: "Dirección de facturación",
      streetPlaceholder: "Calle y número",
      sites: "Lugares de trabajo",
      sitesHint:
        "Añade otras direcciones donde se trabaja (p. ej. varias propiedades).",
      noSites: "Se trabaja en la dirección de facturación.",
      addSite: "Añadir lugar",
      siteLabel: "Nombre del lugar",
      siteLabelPlaceholder: "p. ej. Jardín Seestrasse",
      documentLanguage: "Idioma de los documentos",
      languageDefault: "Predeterminado de la empresa",
      duplicatesTitle: "Posibles duplicados:",
      match: {
        email: "mismo correo",
        phone: "mismo teléfono",
        name_postalCode: "mismo nombre",
      },
      createAndUse: "Crear y seleccionar",
      since: "Cliente desde el {{date}}",
    },
    request: {
      singular: "Solicitud",
      plural: "Solicitudes",
      new: "Nueva solicitud",
      newSubtitle:
        "Registra el trabajo en el lugar; la oficina lo presupuesta después.",
      listSubtitle:
        "Trabajos registrados en campo, del primer contacto a la oferta.",
      empty: "Todavía no hay solicitudes.",
      scope: { open: "Abiertas", closed: "Cerradas" },
      onlyMine: "Solo mías",
      whatShort: "Qué",
      when: "Cuándo",
      site: "Lugar",
      priority: "Prioridad",
      asap: "Lo antes posible",
      desiredDate: "Fecha concreta",
      flexible: "flexible",
      stepCustomer: "Cliente",
      stepWhere: "Dónde",
      stepWhat: "Qué hay que hacer",
      stepWhen: "Cuándo y con qué urgencia",
      stepPhotos: "Fotos",
      newCustomer: "Crear un cliente nuevo",
      siteBilling: "Dirección de facturación",
      siteOther: "Otra dirección",
      category: "Categoría",
      subcategory: "Tipo de trabajo",
      shortDescription: "Descripción breve",
      shortPlaceholder: "p. ej. Podar el seto junto a la calle",
      details: "Detalles",
      detailsPlaceholder: "Tamaño, acceso, deseos especiales…",
      timeFrom: "Desde",
      timeTo: "Hasta",
      timeFlexible: "La hora es flexible",
      priceExpectation: "Expectativa de precio",
      pricing: {
        offer_required: "Se necesita oferta",
        fixed_price: "Precio fijo acordado",
        time_and_material: "Según esfuerzo",
      },
      fixedAmount: "Importe acordado",
      taxIncluded: "IVA incluido",
      inclVat: "IVA incl.",
      exclVat: "sin IVA",
      assignees: "Asignada a",
      unassigned: "Sin asignar",
      takePhoto: "Hacer foto",
      choosePhotos: "Elegir archivos",
      caption: "Pie de foto (opcional)",
      photosHint: "Las fotos ayudan a la oficina a presupuestar correctamente.",
      photos: "Fotos",
      noPhotos: "Todavía no hay fotos.",
      uploadPhotos_one: "Subir {{count}} archivo",
      uploadPhotos_other: "Subir {{count}} archivos",
      deletePhotoConfirm: "¿Eliminar este archivo?",
      saveDraft: "Guardar borrador",
      submitToOffice: "Enviar a la oficina",
      savedDraft: "Borrador guardado",
      submitted: "Solicitud enviada",
      uploadFailures_one: "No se pudo subir {{count}} archivo.",
      uploadFailures_other: "No se pudieron subir {{count}} archivos.",
      captureAnother: "Registrar otra solicitud",
      viewRequest: "Ver solicitud",
      draftNotice:
        "Esta solicitud es un borrador. Envíala a la oficina cuando esté completa.",
      startReview: "Iniciar revisión",
      createOffer: "Crear oferta",
      invoiceDirectly: "Facturar directamente",
      reopen: "Reabrir",
      decline: "Rechazar",
      declineTitle: "Rechazar solicitud",
      cancelRequest: "Cancelar solicitud",
      cancelTitle: "Cancelar solicitud",
      deleteConfirm: "¿Eliminar esta solicitud y sus fotos?",
      closedReason: {
        declined: "Rechazada: {{reason}}",
        cancelled: "Cancelada: {{reason}}",
      },
    },
    offer: {
      singular: "Oferta",
      plural: "Ofertas",
      new: "Nueva oferta",
      newSubtitle: "Elige el cliente y después añade las posiciones.",
      listSubtitle: "Preparar, enviar y hacer seguimiento de ofertas.",
      empty: "Todavía no hay ofertas.",
      titlePlaceholder: "p. ej. Mantenimiento del jardín 2026",
      createDraft: "Crear borrador",
      fromRequest: "Creada a partir de la solicitud {{number}}.",
      validUntil: "Válida hasta",
      accept: "Aceptar",
      decline: "Rechazar",
      acceptTitle: "Marcar oferta como aceptada",
      declineTitle: "Marcar oferta como rechazada",
      decisionNote: "Nota",
      decisionNotePlaceholder: "p. ej. aceptada por teléfono el …",
      revise: "Crear revisión",
      openRevision: "Abrir revisión",
      openLatest: "Abrir la última revisión",
      previousRevision: "Revisión anterior",
      nextRevision: "Nueva revisión",
      lockedNotice:
        "Las ofertas enviadas no se pueden modificar. Crea una revisión para enviar una versión actualizada.",
      revisionInProgress:
        "Se está preparando una revisión de esta oferta. Al enviarla, sustituirá a esta.",
      supersededNotice:
        "Esta oferta fue sustituida por una revisión más reciente.",
      acceptedToInvoice: "Aceptada: las posiciones están listas para facturar.",
      fullyInvoiced: "Aceptada y facturada por completo.",
      createInvoice: "Crear factura",
      deleteDraft: "Eliminar borrador",
      deleteConfirm: "¿Eliminar este borrador de oferta?",
    },
    invoice: {
      singular: "Factura",
      plural: "Facturas",
      new: "Nueva factura",
      newSubtitle: "Factura una oferta aceptada, una solicitud u otra cosa.",
      listSubtitle: "Emitir facturas y seguir los cobros.",
      empty: "Todavía no hay facturas.",
      tabs: {
        draft: "Borradores",
        open: "Abiertas",
        overdue: "Vencidas",
        paid: "Cobradas",
        credit_note: "Notas de crédito",
        cancelled: "Anuladas",
      },
      creditNote: "Nota de crédito",
      draftNumber: "Borrador",
      draftCreditNote: "Borrador de nota de crédito",
      createDraft: "Crear borrador",
      draftHint:
        "El número y la fecha se asignan al emitir. Hasta entonces puedes cambiarlo todo.",
      fromOffer:
        "Se copian todas las posiciones de la oferta {{number}} aún no facturadas.",
      fromRequest: "Factura de la solicitud {{number}}.",
      draftNotice:
        "Borrador: editable libremente. Al emitirla, la factura recibe número y fecha y queda congelada.",
      ibanMissing:
        "No hay cuenta bancaria configurada. Añade el IBAN en Ajustes → Facturación.",
      issue: "Emitir factura",
      issueCredit: "Emitir nota de crédito",
      issueConfirm:
        "¿Emitir esta factura? Recibirá su número definitivo y ya no se podrá editar.",
      issueCreditConfirm:
        "¿Emitir esta nota de crédito? Ya no se podrá editar.",
      dueDate: "Vencimiento",
      paid: "Cobrado",
      credited: "Abonado",
      balance: "Saldo pendiente",
      creditTotal: "Importe abonado",
      billingAddress: "Dirección de facturación",
      contactName: "Persona de contacto",
      reference: "Referencia",
      paymentTerms: "Condiciones de pago",
      termDays: "Plazo de pago (días)",
      corrects: "Corrige",
      cancel: "Anular factura",
      cancelTitle: "Anular factura",
      cancelHint:
        "Anular invalida la factura. Si ya se cobró algo, crea una nota de crédito.",
      cancelledNotice: "Anulada: {{reason}}",
      createCreditNote: "Crear nota de crédito",
      creditHint:
        "Se crea un borrador de nota de crédito con todas las posiciones. Ajústalo antes de emitirlo.",
      deleteDraft: "Eliminar borrador",
      deleteConfirm: "¿Eliminar este borrador?",
    },
    payment: {
      plural: "Cobros",
      record: "Registrar cobro",
      edit: "Editar cobro",
      none: "No hay cobros registrados.",
      amount: "Importe ({{currency}})",
      date: "Fecha de cobro",
      method: "Forma de pago",
      reference: "Referencia",
      deleteConfirm: "¿Eliminar este cobro?",
      methods: {
        bank_transfer: "Transferencia",
        cash: "Efectivo",
        card: "Tarjeta",
        twint: "TWINT",
        other: "Otro",
      },
    },
    project: { singular: "Proyecto", plural: "Proyectos" },
    organization: {
      title: "Organización",
      subtitle:
        "Empresas, departamentos y equipos. Los permisos provienen de los roles.",
      newUnit: "Nueva unidad",
      unit: "Unidad",
      addChild: "Añadir subunidad",
      empty: "Todavía no hay unidades.",
      selectHint: "Selecciona una unidad para editarla.",
      kind: "Tipo",
      kinds: { company: "Empresa", department: "Departamento", team: "Equipo" },
      parent: "Pertenece a",
      noParent: "Nivel superior",
      color: "Color",
      description: "Descripción",
      members: "Miembros",
      noMembers: "Todavía no hay miembros.",
      addMember: "Añadir miembro…",
      lead: "Responsable",
      leadHint:
        "Los responsables se muestran como personas de contacto y no obtienen permisos adicionales.",
    },
    settings: {
      accentColor: "Color de acento",
      accentColorHint: "Se usa en líneas y títulos de los PDF.",
      documentTexts: {
        offerIntroText: "Introducción predeterminada de ofertas",
        offerTermsText: "Condiciones predeterminadas de ofertas",
        invoiceIntroText: "Introducción predeterminada de facturas",
        invoiceTermsText: "Texto predeterminado de condiciones de pago",
        footerText: "Pie de página",
      },
      footerHint: "Una línea, p. ej. empresa, dirección y número fiscal.",
      workTime: "Horario laboral",
      workTimeHint:
        "Valores predeterminados de horarios y vacaciones para nuevos empleados.",
      workdayHours: "Horas por jornada",
      migration: "Versión de la base de datos",
      emails: "Correos salientes",
      emailsHint:
        "Los correos se envían en segundo plano y se reintentan si fallan.",
      noEmails: "Todavía no se han enviado correos.",
      retry: "Reintentar",
    },
    compensation: {
      salaryType: "Tipo de salario",
      monthly: "Salario mensual",
      hourly: "Salario por hora",
      salaryAmount: "Importe",
      salaryBasis: "El importe se refiere a",
      effectivePensum: "Jornada efectiva",
      fullTime: "Jornada completa (100 %)",
      thirteenth: "Paga extra (13.º salario)",
    },
    employees: {
      compensation: "Salario",
      expenses: "Gastos",
      weeklyHours: "Horas semanales",
      workDetails: "Detalles del trabajo",
      workDetailsDescription:
        "Asistencia y reparto por proyectos de esta semana.",
    },
    audit: { actor: "Autor", event: "Evento" },
    notifications: {
      actor: "Autor",
      actorSearch: "Buscar autor",
      audit: "Registro de auditoría",
      created: "Creado",
      entityId: "ID del objeto",
      entityType: "Tipo de objeto",
      event: "Evento",
      metadata: "Detalles",
      noAudit: "No hay eventos.",
      rows_one: "{{count}} fila",
      rows_other: "{{count}} filas",
      summary: "Resumen",
    },
    catalog: {
      addComponent: "Añadir artículo",
      components: "componentes",
      defaultLocation: "Ubicación predeterminada",
      item: "Artículo",
      itemsAlwaysTracked: "Los artículos siempre se gestionan en inventario.",
      kind: "Tipo",
      noPackageItems: "No hay artículos en este paquete.",
      package: "Paquete",
      packageItems: "Contenido del paquete",
      search: "Buscar en el catálogo",
      stockEnabled: "Gestionar el stock del paquete por separado",
    },
    planner: {
      projectListHelp:
        "Los proyectos permanecen en la lista para poder asignarlos a varias personas.",
      projects: "Proyectos",
      team: "Equipo",
    },
    projects: {
      acceptedOffers: "Ofertas aceptadas",
      activity: "Actividad",
      actualHours: "Horas reales",
      allCustomers: "Todos los clientes",
      allMembers: "Todos los miembros",
      assignees: "Personas asignadas",
      assigneesHint:
        "Asigna los empleados que necesites. Se requiere al menos un responsable.",
      budgetVariance: "Desviación del presupuesto",
      budgetVsActual: "Presupuesto frente a real",
      contingency: "Reserva",
      effectiveHours: "Horas efectivas",
      excludedCurrencies: "Gastos en moneda extranjera excluidos:",
      expenseBudget: "Gastos",
      expenses: "Gastos",
      financialSummary: "Resumen financiero",
      hours: "Previsto / efectivo",
      includeChildren: "Incluir subproyectos",
      invoiced: "Facturado",
      manager: "Responsable",
      manualCosts: "Costes registrados",
      margin: "Margen",
      member: "Miembro",
      missingCostRates:
        "{{labor}} registros de tiempo y {{material}} movimientos de inventario no tienen tarifa de coste.",
      paid: "Cobrado",
      plannedHours: "Horas previstas",
      toggleChildren: "Mostrar u ocultar subproyectos",
      totalCost: "Coste total",
    },
    time: {
      addManualEntry: "Añadir registro manual",
      clockSessions: "Fichajes",
      compensationBalance: "Saldo de horas",
      dayRecorded: "Registrado el día seleccionado",
      editClockEntry: "Editar fichaje",
      manualEntryHint:
        "Selecciona un registro abajo para cambiar su fecha u horas.",
    },
  },
};
